import Value from "aldoo-ra/Value"
import { useEffect, useRef } from "react"
import { GetUserProfile } from "../api"
import { usePromise } from "aldoo-ra/usePromise"

export default function Profile() {
    //get the user-credentials value and watch for changes
    //if there is a valid credentials object, we will get the user profile
    const [auth] = Value("user-credentials")
    const [, setProfile] = Value({
        key: "user-profile",
        defaultValue: null,
        persistKey: "user-profile",
    })


    const { result, execute, pending, reset } = usePromise(async () => {
        //iek is inflight encryption key used to decode passwords and texts
        return await GetUserProfile({
            fields: "name avatar email devices iek",
        })
    }, true)

    // Identity the current result/profile was fetched for — login paths only
    // setAuth, so an account switch (A → B without an auth=null step) must be
    // detected here or the previous user's profile is served forever.
    const fetchedFor = useRef()

    useEffect(() => {
        //reset the profile if there are no credentials
        if (!auth) {
            // reset/execute are absent while a fetch is in-flight (usePromise pending state),
            // so guard the calls — clearing auth mid-fetch must not crash.
            fetchedFor.current = undefined
            reset?.()
            return setProfile(undefined)
        }
        // Only execute if we have auth with a valid token to prevent race conditions
        // during OAuth signin where auth object exists but token hasn't propagated yet
        if (!auth.auth_token) return

        const identity = auth._id || auth.email
        // Different user logged in — drop the stale result and refetch.
        // Keep fetchedFor as the old identity until reset is available (it is
        // absent mid-fetch), so a stale in-flight result can never be stored.
        if (fetchedFor.current && fetchedFor.current !== identity) {
            setProfile(undefined)
            if (reset) {
                fetchedFor.current = undefined
                reset()
            }
            return
        }
        if (!pending && !result) {
            fetchedFor.current = identity
            execute?.()
        }
        if (result && !result.error && fetchedFor.current === identity) setProfile(result)

    }, [auth, result])

}

/**
 * Refresh the user profile - can be called from anywhere to update profile data
 * @param {string} fields - Optional fields to fetch (defaults to standard fields + account_type)
 * @returns {Promise<object>} The updated profile object
 */
export async function refreshProfile(fields = "name avatar email devices iek account_type") {
    try {
        // Import setValue dynamically to avoid hook issues
        const { setValue } = await import("aldoo-ra/Value")
        const updatedProfile = await GetUserProfile({ fields })
        if (updatedProfile && !updatedProfile.error) {
            // Use the non-hook setValue function
            setValue('user-profile', updatedProfile, 'user-profile')
            return updatedProfile
        }
        return null
    } catch (error) {
        console.error("Failed to refresh user profile:", error)
        return null
    }
}